import { Button, ButtonSize } from '@vimily/bonjoro-ui-components-v2/Button';
import { Input } from '@vimily/bonjoro-ui-components-v2/Input';
import { ButtonVariant } from '@vimily/bonjoro-ui-components/Button';
import React, { useContext } from 'react';
import { TeamMemberSelector } from '~/App/Components/Filters/TeamMemberSelector';
import ProfileSearchBox from '~/App/Components/ProfileSearchBox';
import { Profile } from '~/types/models';
import TaskCreationContext from './Context';
import cx from 'classnames';
import { TaskCreationAction } from './TaskCreationWrapper';

const buttonClassName = 'leading-[18px]';

export enum TaskCreationFormInterfaceType {
  Compact = 'compact',
  Portrait = 'portrait',
}

export interface TaskCreationFormProps {
  className?: string;
  actionPanelClassName?: string;
  actions?: TaskCreationAction[];
  title?: string;
  interfaceType?: TaskCreationFormInterfaceType;
  dropdownWidth?: number;
}

export function TaskCreationForm({
  className,
  actionPanelClassName,
  actions = [
    TaskCreationAction.AddAnother,
    TaskCreationAction.RecordLater,
    TaskCreationAction.RecordNow,
  ],
  title = '',
  interfaceType = TaskCreationFormInterfaceType.Compact,
  dropdownWidth = 290,
}: TaskCreationFormProps) {
  const {
    loading,
    email,
    setEmail,
    name,
    setName,
    description,
    setDescription,
    assignee,
    setAssignee,
    errorFields,
    onSubmit,
  } = useContext(TaskCreationContext);

  return (
    <div className={cx(className, 'flex flex-col gap-y-5 p-8')}>
      {title && <span className='text-[21px] font-bold'>{title}</span>}
      <div className='flex flex-col gap-y-4'>
        <div
          className={cx('flex flex-col gap-4', {
            'sm:flex-row':
              interfaceType === TaskCreationFormInterfaceType.Compact,
          })}
        >
          <div className='flex flex-col flex-1 gap-y-1'>
            <label className='text-base font-bold'>Email address*</label>
            <ProfileSearchBox
              value={email}
              onInputChange={setEmail}
              onProfileSelect={(profile: Profile) => {
                setEmail(profile.email);
                setName(profile.name ?? '');
              }}
              disabled={loading}
              placeholder='johndoe@bonjoro.com'
            />
            {errorFields.includes('email') && (
              <span className='text-xs v2-text-orange-600'>
                Invalid email address
              </span>
            )}
          </div>
          <div className='flex flex-col flex-1 gap-y-1'>
            <label className='text-base font-bold flex flex-row gap-x-3'>
              <span>Name</span>
            </label>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setName(e.target.value);
              }}
              disabled={loading}
              placeholder='John Doe'
            />
            {errorFields.includes('name') && (
              <span className='text-xs v2-text-orange-600'>
                Please enter a name
              </span>
            )}
          </div>
        </div>

        <div
          className={cx('flex gap-4 flex-col', {
            'sm:flex-row':
              interfaceType === TaskCreationFormInterfaceType.Compact,
          })}
        >
          <div className='flex flex-col flex-1 gap-y-1'>
            <label className='text-base font-bold'>
              Task description (Optional)
            </label>
            <Input
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDescription(e.target.value)
              }
              disabled={loading}
              placeholder='Eg. New sign up'
            />
          </div>
          <div className='flex flex-col flex-1 gap-y-1'>
            <label className='text-base font-bold'>
              Assign it to (Optional)
            </label>
            <TeamMemberSelector
              className='!w-full'
              bodyContainerClassName='!w-full hover:!v2-border-yellow-500 !v2-border-neutral-300'
              bodyPlaceholder=''
              width={dropdownWidth}
              selectedUser={assignee}
              onSelect={setAssignee}
              disabled={loading}
              hideAllSelector
            />
          </div>
        </div>
      </div>

      <div
        className={cx(
          actionPanelClassName,
          'flex flex-row justify-between items-center mt-2 mb-5'
        )}
      >
        {actions.includes(TaskCreationAction.AddAnother) && (
          <span
            className='v2-text-neutral-500 hover:v2-text-neutral-900 underline font-thin cursor-pointer'
            onClick={() => onSubmit(TaskCreationAction.AddAnother)}
          >
            Create & add another
          </span>
        )}
        <div className='flex flex-row gap-x-6'>
          {actions.includes(TaskCreationAction.RecordLater) && (
            <Button
              className={buttonClassName}
              variant={ButtonVariant.Secondary}
              size={ButtonSize.Medium}
              disabled={loading}
              onClick={() => onSubmit(TaskCreationAction.RecordLater)}
            >
              Add and record later
            </Button>
          )}
          {actions.includes(TaskCreationAction.RecordNow) && (
            <Button
              className={buttonClassName}
              variant={ButtonVariant.Primary}
              size={ButtonSize.Medium}
              disabled={loading}
              onClick={() => onSubmit(TaskCreationAction.RecordNow)}
            >
              Record now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
